import React, { useEffect, useState } from "react";
import { MessageCircle, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const EMPTY = { access_token: "", phone_number_id: "", template_name: "", template_lang: "", display_phone: "" };

export function WhatsAppSenderCard({ isOwner }) {
  const { t } = useLang();
  const [st, setSt] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => API.get("/shop/whatsapp-sender").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!st) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.put("/shop/whatsapp-sender", { ...form, template_name: form.template_name || null, template_lang: form.template_lang || null, display_phone: form.display_phone || null });
      toast.success(t("msg.saved")); setForm(EMPTY); load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(t("wa.clear_confirm"))) return;
    try { await API.delete("/shop/whatsapp-sender"); toast.success(t("msg.saved")); load(); } catch (err) { toast.error(fmtErr(err)); }
  };
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/shop/whatsapp-sender/test", { to: testTo });
      data.ok ? toast.success(`${t("wa.test_ok")} (${data.source})`) : toast.error(`${t("wa.test_fail")}: ${data.error || ""}`);
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const badge = st.active_source === "tenant" ? "bg-emerald-100 text-emerald-800" : st.active_source === "platform" ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-600";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="whatsapp-sender-card">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <MessageCircle className="h-5 w-5 text-green-600" />
        <h3 className="font-heading text-lg font-medium text-slate-900">{t("wa.title")}</h3>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badge}`} data-testid="wa-active-source">
          {st.active_source ? t(`wa.source_${st.active_source}`) : t("wa.source_none")}
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-4">{t("wa.desc")}</p>
      {st.tenant_configured && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3 text-xs mb-4 flex flex-wrap items-center gap-3" data-testid="wa-tenant-config">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          <span>Phone ID <span className="font-mono">{st.phone_number_id}</span>{st.display_phone ? ` (${st.display_phone})` : ""} · token <span className="font-mono">{st.token_hint}</span>{st.template_name ? ` · template ${st.template_name}/${st.template_lang || "en_US"}` : ""}</span>
          <span className="text-slate-500">{st.updated_at?.slice(0, 10)}</span>
          {isOwner && <button onClick={clear} data-testid="wa-clear-button" className="ml-auto inline-flex items-center gap-1 text-red-700 hover:underline"><Trash2 className="h-3.5 w-3.5" />{t("wa.clear")}</button>}
        </div>
      )}
      {isOwner && (
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="wa-form">
          <div className="sm:col-span-2">
            <Label className="text-xs">META_ACCESS_TOKEN</Label>
            <Input data-testid="wa-token-input" type="password" required minLength={20} value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} className="mt-1 h-9 font-mono text-xs" placeholder={t("wa.token_ph")} />
          </div>
          <div><Label className="text-xs">META_PHONE_NUMBER_ID</Label><Input data-testid="wa-phone-id-input" required pattern="[0-9]+" value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} className="mt-1 h-9 font-mono text-xs" /></div>
          <div><Label className="text-xs">{t("wa.display_phone")}</Label><Input value={form.display_phone} onChange={(e) => setForm({ ...form, display_phone: e.target.value })} className="mt-1 h-9 text-xs" placeholder="+670 …" /></div>
          <div><Label className="text-xs">{t("wa.template")}</Label><Input value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })} className="mt-1 h-9 font-mono text-xs" placeholder="daily_digest" /></div>
          <div><Label className="text-xs">{t("wa.template_lang")}</Label><Input value={form.template_lang} onChange={(e) => setForm({ ...form, template_lang: e.target.value })} className="mt-1 h-9 font-mono text-xs" placeholder="en_US / pt_PT" /></div>
          <div className="sm:col-span-2 flex justify-end"><Button type="submit" data-testid="wa-save-button" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9">{t("wa.save")}</Button></div>
        </form>
      )}
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
        <Input data-testid="wa-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={t("wa.test_ph")} className="h-9 w-48 text-xs" />
        <Button type="button" variant="outline" data-testid="wa-test-button" onClick={test} disabled={busy || !testTo || !st.active_source} className="h-9 text-xs">{t("wa.test")}</Button>
        {st.last_test && (
          <span className={`text-xs ${st.last_test.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="wa-last-test">
            {t("wa.last_test")}: {st.last_test.ok ? "OK" : "FAIL"} · {st.last_test.to} · {st.last_test.at?.slice(0, 16).replace("T", " ")}{st.last_test.error ? ` · ${st.last_test.error.slice(0, 120)}` : ""}
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-3">{t("wa.help")}</p>
    </div>
  );
}
